import { api } from "./axiosInstance";

export type PollstarBusinessType = "Concert" | "Theater" | "Corporate" | "Ignore" | "Other";
export type PollstarRegionBucket = "NorthAmerica" | "Europe" | "Other";
export type PollstarMappingStatus = "Pending" | "Mapped";

export type GenreMapping = {
  id: string;
  pollstar_value: string;
  business_type: PollstarBusinessType;
  subcategory_name: string | null;
  status: PollstarMappingStatus;
  mapped_by: string | null;
  mapped_at: string | null;
};

export type ArtistTypeMapping = {
  id: string;
  pollstar_value: string;
  business_type: PollstarBusinessType;
  status: PollstarMappingStatus;
  mapped_by: string | null;
  mapped_at: string | null;
};

export type DerivationFailure = {
  artist_id: number | null;
  trigger: string | null;
  error: string | null;
  ran_at: string;
};

export type PollstarStatus = {
  pending_count: number;
  last_sync_at: string | null;
  last_backfill_at: string | null;
  last_derivation_at: string | null;
  recent_failures: DerivationFailure[];
};

export type SyncSchedule = {
  runs_per_day: number;
  start_hour: number;
  time_zone: string;
  gap_threshold_days: number;
  run_times: string[];
};

const MAPPINGS = "/api/v1/pollstar/mappings";
const ADMIN = "/api/v1/pollstar/admin";

// ── Mappings ──

export const apiGetGenreMappings = async (status?: PollstarMappingStatus): Promise<GenreMapping[]> => {
  const params = status ? { status } : undefined;
  const res = await api.get(`${MAPPINGS}/genres`, { params });
  return res.data;
};

export const apiGetArtistTypeMappings = async (status?: PollstarMappingStatus): Promise<ArtistTypeMapping[]> => {
  const params = status ? { status } : undefined;
  const res = await api.get(`${MAPPINGS}/artist-types`, { params });
  return res.data;
};

export const apiGetPendingCount = async (): Promise<number> => {
  const res = await api.get<{ pending_count: number }>(`${MAPPINGS}/pending-count`);
  return res.data.pending_count;
};

export const apiMapGenre = async (
  id: string,
  businessType: PollstarBusinessType,
  subcategoryName: string | null,
): Promise<GenreMapping> => {
  const res = await api.patch(`${MAPPINGS}/genres/${id}`, {
    business_type: businessType,
    subcategory_name: subcategoryName,
  });
  return res.data;
};

export const apiBulkMapGenres = async (
  ids: string[],
  businessType: PollstarBusinessType,
  subcategoryName: string | null,
): Promise<number> => {
  const res = await api.post<{ updated: number }>(`${MAPPINGS}/genres/bulk`, {
    ids,
    business_type: businessType,
    subcategory_name: subcategoryName,
  });
  return res.data.updated;
};

export const apiMapArtistType = async (
  id: string,
  businessType: PollstarBusinessType,
): Promise<ArtistTypeMapping> => {
  const res = await api.patch(`${MAPPINGS}/artist-types/${id}`, { business_type: businessType });
  return res.data;
};

// ── Admin ──

export const apiGetPollstarStatus = async (): Promise<PollstarStatus> => {
  const res = await api.get(`${ADMIN}/status`);
  return res.data;
};

export const apiGetSyncSchedule = async (): Promise<SyncSchedule> => {
  const res = await api.get(`${ADMIN}/sync-schedule`);
  return res.data;
};

export const apiUpdateSyncSchedule = async (
  runsPerDay: number,
  startHour: number,
  timeZone: string,
  gapThresholdDays: number,
): Promise<SyncSchedule> => {
  const res = await api.put(`${ADMIN}/sync-schedule`, {
    runs_per_day: runsPerDay,
    start_hour: startHour,
    time_zone: timeZone,
    gap_threshold_days: gapThresholdDays,
  });
  return res.data;
};

export const apiTriggerBackfill = async (): Promise<void> => {
  await api.post(`${ADMIN}/backfill`);
};

export const apiTriggerRebuild = async (): Promise<void> => {
  await api.post(`${ADMIN}/rebuild`);
};

export const apiPreviewGenreRemap = async (id: string): Promise<number> => {
  const res = await api.get<{ affected_count: number }>(`${ADMIN}/preview-remap/genre/${id}`);
  return res.data.affected_count;
};
